import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label } from '@1trading/react';
import { useId, useState } from 'react';
import { closeOnly } from '@/components/dialogs/close-only';
import { FieldError } from '@/components/ui/field-error';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { omit } from '@/utils/object';
import { DowntimeRuleFields } from '../downtime-rule-dialog';
import { findDowntimeRuleProblems, marketMakerOptions } from '../mm-program-editing';
import type { DowntimeRuleInput } from '../mm-program-schema';
import type { DowntimeRuleTerms } from './downtime-groups';

export interface DowntimeTarget {
  pid: string;
  name: string;
  active: boolean;
  /** The markets downtime can be set on */
  markets: string[];
}

export interface DowntimeGroupDraft {
  pid: string;
  instruments: string[];
  /** The id is only there for the rule form's checks; each market's copy gets its own on save */
  rule: DowntimeRuleInput;
}

interface DowntimeGroupDialogProps {
  /** Market makers a new rule can go on; an edited rule stays on its own */
  targets: DowntimeTarget[];
  initial: DowntimeGroupDraft;
  isNew: boolean;
  onClose: () => void;
  onSave: (pid: string, terms: DowntimeRuleTerms, instruments: string[]) => void;
}

export const DowntimeGroupDialog = ({ targets, initial, isNew, onClose, onSave }: DowntimeGroupDialogProps) => {
  const [pid, setPid] = useState(initial.pid);
  const [instruments, setInstruments] = useState(initial.instruments);
  const [rule, setRule] = useState(initial.rule);
  // Problems show once a save is attempted, so a blank new rule does not open full of errors.
  const [hasAttempted, setHasAttempted] = useState(false);
  const marketMakerId = useId();
  const marketsId = useId();
  const target = targets.find((item) => item.pid === pid);
  const markets = target?.markets ?? [];
  const kind = rule.type === 'recurring' ? 'recurring' : 'one-off';

  const findProblems = () => ({
    ...findDowntimeRuleProblems(rule),
    ...(pid ? {} : { pid: 'Pick a market maker.' }),
    ...(pid && !instruments.length ? { instruments: 'Pick at least one market.' } : {}),
  });
  const problems: Partial<Record<string, string>> = hasAttempted ? findProblems() : {};

  // A new rule covers all of the market maker's markets until some are unpicked.
  const pickMarketMaker = (next: string) => {
    setPid(next);
    setInstruments(targets.find((item) => item.pid === next)?.markets ?? []);
  };

  const save = () => {
    setHasAttempted(true);
    if (!Object.keys(findProblems()).length) onSave(pid, omit(rule, 'id'), instruments);
  };

  return (
    <Dialog onOpenChange={closeOnly(onClose)} open>
      <DialogContent aria-describedby={undefined} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? `Add ${kind} downtime` : `Edit ${kind} downtime`}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={marketMakerId}>Market maker</Label>
            {isNew ? (
              <SearchableSelect
                aria-invalid={Boolean(problems.pid)}
                empty="No market makers with perpetual programs"
                id={marketMakerId}
                onChange={pickMarketMaker}
                options={marketMakerOptions(targets)}
                placeholder="Pick a market maker"
                searchPlaceholder="Search market makers…"
                value={pid}
              />
            ) : (
              <p className="text-sm font-medium" id={marketMakerId}>
                {target?.name ?? pid}
              </p>
            )}
            <FieldError message={problems.pid} />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor={marketsId}>Markets</Label>
              {markets.length > 1 ? (
                <Button className="h-auto p-0 text-xs" onClick={() => setInstruments(instruments.length === markets.length ? [] : markets)} size="sm" type="button" variant="link">
                  {instruments.length === markets.length ? 'Clear' : 'All markets'}
                </Button>
              ) : null}
            </div>
            <SearchableMultiSelect
              aria-invalid={Boolean(problems.instruments)}
              disabled={!pid}
              empty="No markets found"
              id={marketsId}
              onChange={(next) => setInstruments(markets.filter((market) => next.includes(market)))}
              options={markets.map((market) => ({ value: market, label: market }))}
              placeholder={pid ? 'Pick markets' : 'Pick a market maker first'}
              searchPlaceholder="Search markets…"
              value={instruments}
            />
            <FieldError message={problems.instruments} />
            {target && markets.length > 1 && instruments.length === markets.length ? (
              <p className="text-xs text-muted-foreground">A market added to {target.name} later does not get this rule; add it here then.</p>
            ) : null}
          </div>
          <DowntimeRuleFields draft={rule} onChange={setRule} problems={problems} />
        </div>
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button onClick={save} type="button">
            {isNew ? 'Add rule' : 'Update rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
DowntimeGroupDialog.displayName = 'DowntimeGroupDialog';
